<ng-container *transloco="let t; read: 'layouts'">
<app-grid-page [title]="t('title')">
  <app-grid
    [columns]="activeColumns()"
    [data]="activeData()"
    [rowId]="activeRowId()"
    [loading]="isLoading()"
    [tabs]="gridTabs()"
    [activeTabId]="tab()"
    [searchable]="true"
    [detailEnabled]="true"
    detailMode="overlay"
    [detailWidth]="380"
    [selectedRow]="selectedRow()"
    [rowActions]="activeRowActions()"
    [primaryAction]="{
      label: tab() === 'groups' ? t('add.group') : t('add.template'),
      icon: 'icon-content-add24px'
    }"
    (tabChange)="onTabChange($event)"
    (searchChange)="onSearch($event)"
    (rowClick)="onRowClick($event)"
    (rowDblClick)="onRowDblClick($event)"
    (rowActionClick)="onRowAction($event.actionId, $event.row)"
    (primaryActionClick)="onPrimaryAction()"
  >
    <!-- Cell: template name + owning group subline -->
    <ng-template gridCell="templateName" let-row>
      @let r = asTemplate(row);
      <div class="min-w-0">
        <div class="truncate text-[14px] font-semibold text-ink-1">{{ r.layout.layoutId }}</div>
        <div class="mt-0.5 truncate text-[12px] text-ink-2">{{ r.group.groupId }}</div>
      </div>
    </ng-template>

    <!-- Cell: event pill -->
    <ng-template gridCell="event" let-row>
      @let l = asTemplate(row).layout;
      <app-label-badge [dot]="eventDot(l)">{{ eventLabel(l) }}</app-label-badge>
    </ng-template>

    <!-- Detail pane — rendered off the component's own detail() signal so it
         survives an edit/refresh and branches on template vs group. -->
    <ng-template gridDetail>
      @if (detailTemplate(); as row) {
        <div class="flex flex-col gap-4">
          <!-- Preview placeholder (no screen-content data on the API). -->
          <div class="flex h-[200px] flex-none items-center justify-center rounded-card bg-sunken">
            <div class="flex h-[150px] w-[112px] flex-col gap-2 rounded-[2px] bg-card p-2.5 shadow-hairline">
              <div class="h-4 rounded-[1px] bg-ink-1/80"></div>
              <div class="h-2 rounded-[1px] bg-hairline"></div>
              <div class="h-2 rounded-[1px] bg-hairline"></div>
              <div class="h-2 w-3/5 rounded-[1px] bg-hairline"></div>
            </div>
          </div>
          <div class="grid grid-cols-[110px_1fr] items-center gap-x-3 gap-y-3 text-[14px] text-ink-1">
            <span class="text-[11px] font-semibold uppercase tracking-wide text-ink-2">{{ t('detail.group') }}</span>
            <span class="truncate">{{ row.group.groupId }}</span>
            <span class="text-[11px] font-semibold uppercase tracking-wide text-ink-2">{{ t('detail.order') }}</span>
            <span>{{ row.layout.groupOrderNum ?? '—' }}</span>
            <span class="text-[11px] font-semibold uppercase tracking-wide text-ink-2">{{ t('detail.showsWhen') }}</span>
            <app-label-badge [dot]="eventDot(row.layout)">{{ eventLabel(row.layout) }}</app-label-badge>
            <span class="text-[11px] font-semibold uppercase tracking-wide text-ink-2">{{ t('detail.duration') }}</span>
            <span>{{ templateDuration(row.layout) }}</span>
          </div>
          <div class="h-px bg-hairline"></div>
          <div class="break-all font-num text-[12px] text-ink-3">{{ t('detail.templateId', { id: row.layout.layoutId }) }}</div>
          <div class="mt-2 flex flex-col gap-2.5">
            <button
              type="button"
              (click)="editTemplate(row)"
              class="w-full rounded-control bg-action px-4 py-2.5 text-[14px] font-semibold text-action-text hover:brightness-90"
            >
              {{ t('detail.configureTemplate') }}
            </button>
            <button
              type="button"
              (click)="editLayout()"
              class="w-full rounded-control border border-hairline bg-card px-4 py-2.5 text-[14px] font-semibold text-ink-1 hover:bg-sunken"
            >
              {{ t('detail.editLayout') }}
            </button>
            <app-button class="w-full" variant="primary" size="sm" destructive iconLeft="icon-delete-trash" [label]="t('detail.delete')" (activated)="confirmDeleteTemplate(row)" />
          </div>
        </div>
      } @else if (detailGroup(); as group) {
        <div class="flex flex-col gap-4">
          <div class="grid grid-cols-[110px_1fr] items-center gap-x-3 gap-y-3 text-[14px] text-ink-1">
            <span class="text-[11px] font-semibold uppercase tracking-wide text-ink-2">{{ t('detail.dates') }}</span>
            <span>{{ group.startDate ?? '—' }} → {{ group.endDate ?? '—' }}</span>
            <span class="text-[11px] font-semibold uppercase tracking-wide text-ink-2">{{ t('detail.hours') }}</span>
            <span>{{ group.startTime ?? '—' }} → {{ group.endTime ?? '—' }}</span>
            <span class="text-[11px] font-semibold uppercase tracking-wide text-ink-2">{{ t('detail.days') }}</span>
            <span>{{ daysLabel(group) }}</span>
            <span class="text-[11px] font-semibold uppercase tracking-wide text-ink-2">{{ t('detail.slideshow') }}</span>
            <span>{{ slideshowLabel(group) }}</span>
          </div>
          <div class="h-px bg-hairline"></div>
          <div class="text-[11px] font-semibold uppercase tracking-wide text-ink-2">
            {{ t('detail.templatesCount', { count: templatesInGroup(group).length }) }}
          </div>
          <div class="flex flex-col gap-2.5">
            @for (layout of templatesInGroup(group); track layout.id) {
              <div class="flex items-center justify-between gap-2 text-[14px] text-ink-1">
                <span class="truncate">{{ layout.layoutId }}</span>
                <app-label-badge [dot]="eventDot(layout)">{{ eventLabel(layout) }}</app-label-badge>
              </div>
            } @empty {
              <span class="text-[13px] text-ink-3">{{ t('detail.noTemplates') }}</span>
            }
          </div>
          <div class="h-px bg-hairline"></div>
          <div class="mt-2 flex flex-col gap-2.5">
            <button
              type="button"
              (click)="editGroup(group)"
              class="w-full rounded-control bg-action px-4 py-2.5 text-[14px] font-semibold text-action-text hover:brightness-90"
            >
              {{ t('detail.configureGroup') }}
            </button>
            <button
              type="button"
              (click)="openAssignDialog(group)"
              class="w-full rounded-control border border-hairline bg-card px-4 py-2.5 text-[14px] font-semibold text-ink-1 hover:bg-sunken"
            >
              {{ t('detail.assign') }}
            </button>
            <app-button class="w-full" variant="primary" size="sm" destructive iconLeft="icon-delete-trash" [label]="t('detail.delete')" (activated)="confirmDeleteGroup(group)" />
          </div>
        </div>
      }
    </ng-template>
  </app-grid>
</app-grid-page>
</ng-container>

@if (isGroupDialogOpen()) {
  <app-layout-group-dialog
    [mode]="groupDialogMode()"
    [group]="editingGroup()"
    (close)="closeGroupDialog()"
    (save)="handleGroupSave($event)"
  ></app-layout-group-dialog>
}

@if (isLayoutDialogOpen() && layoutDialogGroup()) {
  <app-layout-dialog
    [mode]="layoutDialogMode()"
    [layout]="editingLayout()"
    [group]="layoutDialogGroup()!"
    (close)="closeLayoutDialog()"
    (save)="handleLayoutSave($event)"
  ></app-layout-dialog>
}

@if (isAssignDialogOpen() && assignGroup()) {
  <app-assign-device-dialog
    [group]="assignGroup()!"
    (close)="closeAssignDialog()"
  ></app-assign-device-dialog>
}
